/**
 * AdminLogin — email/password gate for /admin.
 *
 * Independent of the regular user auth flow. Even if the user is signed
 * in to a regular Neyra account, /admin requires this extra check.
 *
 * On success:
 *  - Sets the admin gate flag in `sessionStorage` (auto-clears when the
 *    browser tab is closed).
 *  - Navigates to /admin (or the originally requested admin path).
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, ShieldCheck, AlertTriangle } from "lucide-react";
import {
    verifyAdminCredentials,
    openAdminGate,
    isAdminGateOpen,
    adminUsesDefaultCredentials,
    adminExpectedEmail,
} from "@/lib/adminGate";
import Logo from "@/components/Logo";

const MAX_ATTEMPTS = 5;

const AdminLogin = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const redirectTo = location.state?.from || "/admin";

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [error, setError] = useState(null);
    const [attempts, setAttempts] = useState(0);
    const [busy, setBusy] = useState(false);

    // Already authenticated for admin? Skip the form.
    useEffect(() => {
        if (isAdminGateOpen()) {
            navigate(redirectTo, { replace: true });
        }
    }, [navigate, redirectTo]);

    const submit = (e) => {
        e?.preventDefault?.();
        if (busy) return;
        if (attempts >= MAX_ATTEMPTS) {
            setError("Too many failed attempts. Refresh the page to try again.");
            return;
        }
        setBusy(true);
        setError(null);
        // Tiny delay so brute-forcers can't hammer this in a tight loop.
        setTimeout(() => {
            if (!verifyAdminCredentials(email, password)) {
                setAttempts((a) => a + 1);
                setError("Incorrect admin email or password.");
                setBusy(false);
                return;
            }
            openAdminGate();
            setBusy(false);
            navigate(redirectTo, { replace: true });
        }, 350);
    };

    const showDefaultsWarning = adminUsesDefaultCredentials();

    return (
        <div
            className="min-h-screen w-full bg-background text-foreground flex items-center justify-center px-5"
            data-testid="admin-login-screen"
        >
            <div className="w-full max-w-sm">
                <div className="flex flex-col items-center text-center mb-7">
                    <div className="h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-3">
                        <Logo size={32} />
                    </div>
                    <h1 className="font-display text-[22px] font-semibold tracking-tight inline-flex items-center gap-2">
                        <ShieldCheck size={18} className="text-primary" />
                        Admin sign in
                    </h1>
                    <p className="mt-1 text-[13px] text-muted-foreground max-w-[260px]">
                        Enter the administrator email and password to access the Neyra admin panel.
                    </p>
                </div>

                {showDefaultsWarning && (
                    <div
                        data-testid="admin-login-defaults-warning"
                        className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-[11.5px] text-amber-700 dark:text-amber-300 inline-flex items-start gap-2"
                    >
                        <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                        <span>
                            Admin credentials are using built-in defaults. Set{" "}
                            <code className="font-mono">REACT_APP_ADMIN_EMAIL</code> and{" "}
                            <code className="font-mono">REACT_APP_ADMIN_PASSWORD</code> in your env
                            before deploying to production. Default email:{" "}
                            <code className="font-mono">{adminExpectedEmail()}</code>.
                        </span>
                    </div>
                )}

                <form
                    onSubmit={submit}
                    className="space-y-3"
                    data-testid="admin-login-form"
                    autoComplete="off"
                >
                    <label className="block">
                        <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                            Admin email
                        </span>
                        <input
                            data-testid="admin-login-email"
                            type="email"
                            value={email}
                            onChange={(ev) => { setEmail(ev.target.value); if (error) setError(null); }}
                            placeholder="admin@yourdomain.com"
                            autoComplete="username"
                            className={[
                                "w-full h-14 px-4 rounded-2xl border text-[15px] outline-none transition-colors",
                                error
                                    ? "border-destructive bg-destructive/5"
                                    : "border-border bg-secondary focus:bg-background focus:border-primary",
                            ].join(" ")}
                        />
                    </label>

                    <label className="block">
                        <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                            Password
                        </span>
                        <span
                            className={[
                                "flex items-center h-14 px-4 rounded-2xl border transition-colors",
                                error
                                    ? "border-destructive bg-destructive/5"
                                    : "border-border bg-secondary focus-within:border-primary focus-within:bg-background",
                            ].join(" ")}
                        >
                            <input
                                data-testid="admin-login-password"
                                type={showPw ? "text" : "password"}
                                value={password}
                                onChange={(ev) => { setPassword(ev.target.value); if (error) setError(null); }}
                                placeholder="Your admin password"
                                autoComplete="current-password"
                                className="flex-1 bg-transparent outline-none text-[15px]"
                            />
                            <button
                                type="button"
                                data-testid="admin-login-show-password"
                                onClick={() => setShowPw((s) => !s)}
                                className="h-9 w-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground"
                                aria-label={showPw ? "Hide password" : "Show password"}
                            >
                                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </span>
                    </label>

                    {error && (
                        <p
                            data-testid="admin-login-error"
                            className="text-[12.5px] text-destructive font-medium ml-1"
                        >
                            {error}
                        </p>
                    )}

                    <button
                        data-testid="admin-login-submit"
                        type="submit"
                        disabled={busy || attempts >= MAX_ATTEMPTS}
                        className="mt-2 w-full h-14 rounded-2xl text-primary-foreground font-medium text-[15px] active:scale-[0.98] disabled:opacity-60"
                        style={{
                            background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                            boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                        }}
                    >
                        {busy ? "Verifying…" : "Enter admin panel"}
                    </button>

                    <button
                        type="button"
                        data-testid="admin-login-back"
                        onClick={() => navigate("/auth", { replace: true })}
                        className="w-full text-[12px] text-muted-foreground hover:text-foreground py-2"
                    >
                        Back to Neyra
                    </button>
                </form>
            </div>
        </div>
    );
};

export default AdminLogin;
